import { ChevronLeftIcon, ClockIcon } from '@heroicons/react/24/outline'
import { GlobalConfigType, setLoanDetail } from 'actions'
import cloneDeep from 'clone-deep'
import { LayoutLoading } from 'components/LayoutLoading'
import { LoanDetail } from 'components/LoanDetail'
import { LoanStatus } from 'components/LoanStatus'
import { LoanTags } from 'components/LoanTags'
import { PullThroughRatioPartiesTable } from 'components/PullThroughRatioPartiesTable'
import { resolveContactInfoScreenKey } from 'components/PullThroughRatioPartiesTable/pullThroughRatioScreens'
import { ServicingStatus } from 'components/ServicingStatus'
import { companyName, FieldOrigin, LoanFieldProps } from 'config'
import { usePermissions } from 'hooks/usePermissions'
import { LoanSubmissionDocument } from 'pages/LoanSubmission/types'
import { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Link, useLocation } from 'react-router-dom'
import { toast } from 'react-toastify'
import { getLoanTypeMapForSelect, loanTypeMap } from 'reducers/loanDetail.reducer'
import {
  downloadEntireAuditLog,
  getLoanOverviewOverrides,
  LoanOverviewOverrides,
  loanOverviewSection,
  updateLoanFields,
  updateLoanSubmission,
  uploadFiles,
} from 'services'
import { svgLoading } from 'stories/assets'
import { CopyableText, InputFile, Select, Tooltip } from 'stories/components'
import { openAuditLog, resolveOverviewCopyTemplate, toFileArray, useTitle } from 'utils'
import { setLoanNumber } from 'utils/setLoanNumber'

export function Overview(props: any) {
  const [number, setNumber] = useState('-')
  const [loading, setLoading] = useState(false)
  const [detail, setDetail] = useState({
    loanNumber: '-',
    program: '',
    byteproFileName: 0,
    entityTitle: 'N/A',
    borrowingEntityName: '',
    borrowerName: '-',
    propertyAddress: '-',
    loanAmount: '',
    proposedLoanAmount: '',
    loanStatus: '',
    coBorrowers: [],
  } as Record<string, any>)
  const [action, setAction] = useState('')
  const [overrides, setOverrides] = useState<LoanOverviewOverrides>({})
  const {
    noChangeTitle,
    noCompanyTitle,
    title,
    hasBackButton = true,
    backTitle = '',
    backLink = '',
    showStatusChannel = false,
    additionalInfo = [],
    beforeHeader = null,
    showAuditLog = false,
    showBorrowerUpload,
  } = props

  const [borrower, profile, loanDetail, globalSettingV2, rulesConfig, loanFieldProperties] = useSelector(
    (state: any) => [
      state.borrower,
      state.auth.profile,
      state.loanDetail,
      state.globalConfigReducer[GlobalConfigType.GlobalSettingV2],
      state.globalConfigReducer[GlobalConfigType.RulesConfig],
      state.globalConfigReducer[GlobalConfigType.LoanFields] as LoanFieldProps,
    ],
  )
  const { fileExtensions: acceptedFileTypes } = rulesConfig
  const templateNumber = loanDetail.templateNumber

  // The borrower upload box used to be inferred from the back link, so any borrower screen that
  // returns to the pipeline inherited it. Screens that don't want it pass showBorrowerUpload={false}.
  const isBorrowerOverview = showBorrowerUpload ?? backLink === '/borrowerPipelines'

  const dispatch = useDispatch()
  const location = useLocation()
  const { hasPermission } = usePermissions()

  const manageServicing = hasPermission('Loan.Status.Servicing')

  const ptrScreenKey = useMemo(() => resolveContactInfoScreenKey(location.pathname), [location.pathname])

  const acceptedFileTypeText = useMemo(
    () =>
      [...(acceptedFileTypes || [])]
        .sort()
        .map((v: string) => `.${v}`)
        .join(', '),
    [acceptedFileTypes],
  )

  const entityDisplayName = useMemo(
    () => detail.borrowingEntityName || detail.entityTitle,
    [detail.borrowingEntityName, detail.entityTitle],
  )

  const overviewCopyText = useMemo(
    () =>
      resolveOverviewCopyTemplate(globalSettingV2?.overviewCopyTemplate, {
        loanNumber: detail.loanNumber,
        byteproFileName: detail.byteproFileName,
        borrowingEntityName: detail.borrowingEntityName,
        entityTitle: detail.entityTitle,
        borrowerName: detail.borrowerName,
        propertyAddress: detail.propertyAddress,
        proposedLoanAmount: detail.proposedLoanAmount ?? detail.loanAmount,
        program: detail.program,
        loanStatus: detail.loanStatus,
      }),
    [globalSettingV2?.overviewCopyTemplate, detail],
  )

  useEffect(() => {
    const hasEntityTitleProp = loanFieldProperties?.[FieldOrigin.BorrowerInformation]?.['hasEntityTitle']
    const hasEntityTitle =
      hasEntityTitleProp?.visible === false ? !!borrower.borrower?.entityTitle : borrower.borrower?.hasEntityTitle

    const newEntityTitle = hasEntityTitle ? borrower.borrower?.entityTitle : 'N/A'
    if (detail.entityTitle !== newEntityTitle) {
      let temp = cloneDeep(detail)
      temp.entityTitle = newEntityTitle
      setDetail(temp)
    }
  }, [borrower.borrower?.hasEntityTitle, borrower.borrower?.entityTitle, loanFieldProperties])

  const loanNumber = setLoanNumber()

  // Keyed off the loan number, not mount: routing straight from one loan to another (global
  // search, a loan link) keeps this banner mounted, and it would otherwise keep showing the
  // loan we just left. The request is pinned to that loan, and a response for a loan we have
  // since left is dropped.
  useEffect(() => {
    const requested = loanNumber
    let cancelled = false
    setNumber(String(requested))
    if (requested == 0) return

    setLoading(true)
    Promise.all([loanOverviewSection(requested), getLoanOverviewOverrides(requested).catch(() => ({}))]).then(
      ([data, overridesData]) => {
        if (cancelled) return
        setDetail(data)
        setOverrides(overridesData || {})
        if (props.feedOverview) {
          props.feedOverview(data)
        }
        if (noChangeTitle) {
        } else useTitle(`${data.loanNumber} - ${title}${noCompanyTitle ? '' : ` - ${companyName}`}`)
        setLoading(false)
      },
    )
    return () => {
      cancelled = true
    }
  }, [loanNumber])

  const onUploadNewfile = async (value: any) => {
    if (value !== null) {
      const fileArray = toFileArray(value)
      uploadDocumentFiles(fileArray)
    }
  }

  const uploadDocumentFiles = (files: File[]): Promise<any> => {
    return new Promise((resolve) => {
      const data = {
        path: `loan/${loanNumber}/document`,
      }
      let acceptedFiles: any = []
      files.map((file) => {
        const { name } = file
        const params = name.toLowerCase().split('.')
        const length = params.length
        const ext = params[length - 1]
        if (acceptedFileTypes.includes(ext)) {
          acceptedFiles.push(file)
        } else {
          toast(`${name} is not ${acceptedFileTypes.join(', ')}`, { type: 'error' })
        }
      })
      setLoading(true)

      uploadFiles(data, acceptedFiles).then(async (keys: Array<string>) => {
        const newDocs: LoanSubmissionDocument[] = []
        keys.forEach((key, index) => {
          newDocs.push({
            conditionNo: 0,
            name: acceptedFiles[index].name,
            key,
            status: 'Not Reviewed',
            category: '',
            createdAt: Date.now(),
            generated: false,
            createdBy: profile.email,
            borrower: true,
          })
        })
        await updateLoanSubmission({}, { update: newDocs })
        setLoading(false)
        toast(`Successfully uploaded ${newDocs.length} document${newDocs.length > 1 ? 's' : ''}`, { type: 'success' })
        resolve(true)
        props.onUploadNewFile && props.onUploadNewFile(true)
      })
    })
  }

  const onDownloadEntireAuditLog = async () => {
    setLoading(true)
    await downloadEntireAuditLog()
    setLoading(false)
  }

  const onLoanStatusChange = (key: string, status: string) => {
    dispatch(
      setLoanDetail({
        [key]: status,
      }),
    )
  }

  const onMoveToServicing = () => {
    dispatch(
      setLoanDetail({
        servicingPipeline: true,
        servicingStatus: 'servicing',
      }),
    )
  }

  const changeLoanChannel = async (value: string) => {
    const data = {
      type: Number(value),
    }
    setAction('changeLoanChannel')
    await updateLoanFields(data, !!templateNumber)
    dispatch(
      setLoanDetail({
        loanType: Number(value),
      }),
    )
    setAction('')
    setLoading(false)
  }

  return (
    <div className="Overview-container h-full">
      <div className="shadow1 mb-6 max-w-screen-2xl m-auto bg-white rounded p-3 md:p-7 text-left w-full h-full">
        <div className="relative">
          <LayoutLoading show={loading} />
          {/* Slot for anything a screen wants above the loan header, inside this card - the
              servicing screen puts Related Loans here, where the origination Loan Overview has it. */}
          {beforeHeader}
          {hasBackButton && (
            <div className="text-shade-blue flex items-center mb-2 font-bold">
              <ChevronLeftIcon className="w-4 h-4"></ChevronLeftIcon>
              <Link to={backLink || `/loan_process/overview/${number}`} className="hover:underline">
                {backTitle || 'Return to Loan Overview'}
              </Link>
            </div>
          )}
          <div className="flex flex-wrap justify-between">
            <h1 className="text-2xl font-bold flex items-center mb-2">
              <CopyableText copyText={overviewCopyText}>{title}</CopyableText>
              <span className="text-base ml-3">
                {loading && <img src={svgLoading} className="inline w-6 h-6 text-white animate-spin" />}
              </span>
              {showAuditLog && loanNumber != 0 && (
                <Tooltip message="Download Entire Audit Log">
                  <span className="cursor-pointer ml-3" onClick={onDownloadEntireAuditLog}>
                    <ClockIcon className="w-5 h-5" />
                  </span>
                </Tooltip>
              )}
            </h1>
          </div>
          {loanNumber != 0 && (
            <div className="mb-2">
              <LoanTags loanNumber={loanNumber} />
            </div>
          )}
          <div className="flex flex-wrap gap-4">
            {loanNumber != 0 && (
              <div className="mt-1 flex flex-wrap gap-x-10">
                <LoanDetail
                  loanNumber={loading ? '' : detail.loanNumber}
                  program={loading ? '' : detail.program}
                  byteNumber={loading ? 0 : detail.byteproFileName}
                  entityTitle={loading ? '' : entityDisplayName}
                  borrowerName={loading ? '' : detail.borrowerName}
                  propertyAddress={loading ? '' : detail.propertyAddress}
                  mers={loading ? '' : loanDetail.mers}
                  closingDate={loading ? '' : loanDetail.closingDate}
                  thirdPartyId={loanDetail.thirdPartyId}
                  thirdPartyData={loanDetail.thirdPartyData}
                  config={globalSettingV2}
                  coBorrowers={loading ? [] : detail.coBorrowers}
                  lenders={loading ? [] : detail.lenders || []}
                  additionalInfo={additionalInfo}
                  action={action}
                  onActionChange={setAction}
                  onMergeNewData={() => setAction('mergeNewData')}
                  overrides={overrides}
                  onOverridesChange={setOverrides}
                />
              </div>
            )}
            {showStatusChannel && (
              <div>
                <div className="md:mx-6 flex flex-wrap">
                  <div className="mr-2 text-[14px] w-24">Loan Status:</div>
                  <div>
                    <div className="md:-mt-2">
                      <LoanStatus
                        key={`${loanDetail.loanStatus}-${loanDetail.loanNumber}`}
                        status={loanDetail.loanStatus}
                        servicingPipeline={loanDetail.servicingPipeline}
                        onChange={(status: string) => onLoanStatusChange('loanStatus', status)}
                        onMoveServicing={() => onMoveToServicing()}
                        loanNumber={loanDetail.loanNumber}
                      />
                      {manageServicing && loanDetail.servicingPipeline && loanDetail.loanStatus === 'funded' && (
                        <div className="mt-1">
                          <ServicingStatus
                            key={`${loanDetail.servicingStatus}-${loanDetail.loanNumber}`}
                            status={loanDetail.servicingStatus}
                            onChange={(status: string) => onLoanStatusChange('servicingStatus', status)}
                            loanNumber={loanDetail.loanNumber}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <div className="md:mx-6 flex flex-wrap mt-4 relative">
                  <LayoutLoading show={action === 'changeLoanChannel'} />

                  <div className="mr-2 text-[14px] mt-2 w-24">Channel:</div>
                  <div>
                    {hasPermission('Loan.Overview.Channel') ? (
                      <Select
                        id="loan-type"
                        title=""
                        options={getLoanTypeMapForSelect(loanDetail.loanType)}
                        value={loanDetail.loanType}
                        onChange={changeLoanChannel}
                        history={true}
                        showHistory={() =>
                          openAuditLog({
                            table: 'Loan',
                            field: 'type',
                            keys: { field: 'type', loanNumber: loanDetail.loanNumber },
                            map: loanTypeMap,
                          })
                        }
                      />
                    ) : (
                      <div className="mt-1">{loanTypeMap[loanDetail.loanType]}</div>
                    )}
                  </div>
                </div>
              </div>
            )}
            <PullThroughRatioPartiesTable screenKey={ptrScreenKey} />
          </div>
          {isBorrowerOverview && (
            <div className="absolute right-0 top-0">
              <InputFile
                className="pt-1"
                title=""
                acceptFileTypes={acceptedFileTypeText}
                multiple={true}
                disabled={false}
                showBrowseFiles={false}
                onChange={(value) => onUploadNewfile(value)}
              />
              {props.children && <div className="mt-4">{props.children}</div>}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
